import React, { useState, useEffect } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import { questions } from '../../data/questions';
import { AssessmentData, AssessmentResponse } from '../../types';
import { calculateDoshaScores, generateBodyTypeResult } from '../../utils/scoring';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ProgressBar } from '../ui/ProgressBar';

export function QuizInterface() {
  const { state, setAssessmentData, setResult, setError } = useAssessment();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Record<number, AssessmentResponse>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  useEffect(() => {
    // Initialize assessment data
    if (!state.assessmentData) {
      setAssessmentData({
        responses: {},
        completedAt: new Date()
      });
    }
  }, [state.assessmentData, setAssessmentData]);

  const handleOptionSelect = (dosha: 'vata' | 'pitta' | 'kapha') => {
    const newResponses = {
      ...responses,
      [currentQuestion.id]: {
        questionId: currentQuestion.id,
        dosha
      }
    };
    setResponses(newResponses);
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleCompleteAssessment = () => {
    if (Object.keys(responses).length < totalQuestions) {
      setError('Please answer all questions before completing the assessment.');
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      // Create assessment data
      const assessmentData: AssessmentData = {
        responses,
        completedAt: new Date()
      };

      // Calculate results
      const scores = calculateDoshaScores(assessmentData);
      const result = generateBodyTypeResult(scores);

      // Save assessment data and set result
      setAssessmentData(assessmentData);
      setResult(result);
    } catch (error) {
      setError('An error occurred while processing your assessment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isOptionSelected = (dosha: 'vata' | 'pitta' | 'kapha') => {
    return responses[currentQuestion.id]?.dosha === dosha;
  };

  const canProceed = responses[currentQuestion.id] !== undefined;
  const canComplete = Object.keys(responses).length === totalQuestions;

  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Assessment Not Available</h2>
          <p className="text-gray-600">The assessment questions could not be loaded. Please refresh the page and try again.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            Discover Your Ayurvedic Constitution
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Answer these 33 questions based on your lifelong tendencies to uncover your unique dosha balance
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </span>
            <span className="text-sm font-medium text-gray-700">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <ProgressBar 
            progress={progress} 
            className="h-3 bg-gray-200 rounded-full overflow-hidden"
          />
        </div>

        {/* Question Card */}
        <Card className="mb-8 p-8">
          <div className="mb-6">
            <div className="text-sm text-amber-600 font-medium mb-2">
              {currentQuestion.category}
            </div>
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800 leading-relaxed">
              {currentQuestion.text}
            </h2>
          </div>

          {/* Options */}
          <div className="space-y-4">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionSelect(option.dosha)}
                className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 hover:shadow-md ${
                  isOptionSelected(option.dosha)
                    ? 'border-amber-500 bg-amber-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-amber-300'
                }`}
              >
                <div className="flex items-start">
                  <div className={`w-5 h-5 rounded-full border-2 mr-3 mt-0.5 flex-shrink-0 ${
                    isOptionSelected(option.dosha)
                      ? 'border-amber-500 bg-amber-500'
                      : 'border-gray-300'
                  }`}>
                    {isOptionSelected(option.dosha) && (
                      <div className="w-2 h-2 bg-white rounded-full m-auto"></div>
                    )}
                  </div>
                  <span className={`text-gray-700 leading-relaxed ${
                    isOptionSelected(option.dosha) ? 'font-medium' : ''
                  }`}>
                    {option.text}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            variant="secondary"
            className="px-6 py-3"
          >
            Previous
          </Button>

          <div className="flex gap-3">
            {currentQuestionIndex < totalQuestions - 1 ? (
              <Button
                onClick={handleNext}
                disabled={!canProceed}
                className="px-6 py-3"
              >
                Next Question
              </Button>
            ) : (
              <Button
                onClick={handleCompleteAssessment}
                disabled={!canComplete || isSubmitting}
                className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                {isSubmitting ? 'Processing...' : 'Complete Assessment'}
              </Button>
            )}
          </div>
        </div>

        {/* Error Display */}
        {state.error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-red-800 font-medium">Error: {state.error}</span>
            </div>
            <p className="text-red-600 text-sm mt-1">
              Please review your answers and try again.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}